import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {AllowedTimezone} from 'graphql-client/__generated/graphqlGlobalTypes';
import ControlledTimezonePickerInput from 'i18n/components/ControlledTimezonePickerInput';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {InputChangeEvent} from 'util/Types';

import {NotificationOptOutPreferences, updateNotificationPreferences} from './NotificationPreferences';

interface NotificationPreferenceCheckBoxProps {
  checked: boolean;
  id: string;
  label: string;
  onChange: (e: InputChangeEvent) => void;
  subline?: string;
}

function NotificationPreferenceCheckBox(props: NotificationPreferenceCheckBoxProps): JSX.Element {
  return (
    <div className={css(styles.optOutCheckbox)}>
      <CheckboxInput checked={props.checked} id={props.id} label={props.label} onChange={props.onChange} />
      {props.subline && <div className={css(styles.subline)}>{props.subline}</div>}
    </div>
  );
}

export interface NotificationPreferencesSectionProps {
  notificationOptOutPreferences: NotificationOptOutPreferences;
  deliveryTimezonePreference: AllowedTimezone;
}

export default function NotificationPreferencesSection(
  props: NotificationPreferencesSectionProps
): JSX.Element {
  const [notificationOptOutPreferences, setNotificationOptOutPreferences] =
    useState<NotificationOptOutPreferences>(props.notificationOptOutPreferences);
  const [deliveryTimezonePreference, setDeliveryTimezonePreference] = useState<AllowedTimezone>(
    props.deliveryTimezonePreference
  );

  return (
    <div>
      <h2>Notification Preferences</h2>
      <div>
        {BenchlingConfig.STUDIES_ENABLE && (
          <div>
            <h4>Delivery Timezone</h4>
            <ControlledTimezonePickerInput
              timezone={deliveryTimezonePreference}
              onSelect={(timezone: AllowedTimezone) => {
                setDeliveryTimezonePreference(timezone);
              }}
            />
          </div>
        )}
        <div>
          <h4>Email</h4>
          <NotificationPreferenceCheckBox
            checked={!notificationOptOutPreferences.isOptedOutOfOrgs}
            id="notifications-opt-out-orgs"
            label="Organization membership changes"
            onChange={(e: InputChangeEvent) => {
              setNotificationOptOutPreferences({
                ...notificationOptOutPreferences,
                isOptedOutOfOrgs: !e.target.checked,
              });
            }}
            subline="This includes invitations as well as join requests & confirmations"
          />
          {BenchlingConfig.REQUESTS_V2_ENABLE && (
            <NotificationPreferenceCheckBox
              checked={!notificationOptOutPreferences.isOptedOutOfRequestsV2}
              id="notifications-opt-out-requests-v2"
              label="Request updates"
              onChange={(e: InputChangeEvent) => {
                setNotificationOptOutPreferences({
                  ...notificationOptOutPreferences,
                  isOptedOutOfRequestsV2: !e.target.checked,
                });
              }}
            />
          )}
          {BenchlingConfig.WORKFLOWS_ENABLE && BenchlingConfig.WORKFLOWS_ENABLE_NOTIFICATIONS && (
            <NotificationPreferenceCheckBox
              checked={!notificationOptOutPreferences.isOptedOutOfProcesses}
              id="notifications-opt-out-processes"
              label="Workflow task assignments & status changes"
              onChange={(e: InputChangeEvent) => {
                setNotificationOptOutPreferences({
                  ...notificationOptOutPreferences,
                  isOptedOutOfProcesses: !e.target.checked,
                });
              }}
            />
          )}
          {BenchlingConfig.STUDIES_ENABLE && (
            <NotificationPreferenceCheckBox
              checked={!notificationOptOutPreferences.isOptedOutOfStudies}
              id="notifications-opt-out-studies"
              label="Study events & configured notifications"
              onChange={(e: InputChangeEvent) => {
                setNotificationOptOutPreferences({
                  ...notificationOptOutPreferences,
                  isOptedOutOfStudies: !e.target.checked,
                });
              }}
            />
          )}
        </div>
        <div id="notification-preferences-footer" className={css(styles.footnote)}>
          <a href="http://info.benchling.com/email-preferences.html">
            {' '}
            Manage additional communication preferences.{' '}
          </a>
          Benchling also sends necessary transactional and account activity emails, which cannot be disabled
        </div>
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.SMALL}
          onClick={() =>
            updateNotificationPreferences(notificationOptOutPreferences, deliveryTimezonePreference).then(
              () => {
                notifications.success('Notification preferences successfully updated.');
              }
            )
          }
          text="Save Preferences"
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  optOutCheckbox: {
    marginBottom: 10,
  },
  subline: {
    color: 'gray',
    fontWeight: 500,
    fontSize: 11,
    marginTop: 6,
  },
  footnote: {
    color: 'gray',
    fontSize: 13,
    fontStyle: 'italic',
    fontWeight: 500,
    marginBottom: 12,
  },
});
